import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Tag, Share2, Check, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { blogService, BlogPost } from "@/lib/supabase";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { getBlogMeta } from "@/lib/utils";
import { useWhatsAppPopup } from "@/contexts/WhatsAppPopupContext";

// --- Markdown Renderer Lite (Unifer Model) ---
function renderInline(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className="font-bold text-white">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith("*") && part.endsWith("*")) {
      return (
        <em key={i} className="italic text-slate-300">
          {part.slice(1, -1)}
        </em>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

function renderBody(text: string, onAction: () => void): React.ReactNode[] {
  const blocks: React.ReactNode[] = [];
  let paraLines: string[] = [];
  let key = 0;

  const allLines = text.split("\n");

  const flushParagraph = () => {
    const content = paraLines.join(" ").trim();
    paraLines = [];
    if (!content) return;
    blocks.push(
      <p key={key++} className="text-slate-300 text-lg leading-relaxed mb-8 font-medium">
        {renderInline(content)}
      </p>
    );
  };

  for (const raw of allLines) {
    const line = raw.trim();

    if (line === "") { flushParagraph(); continue; }
    if (line === "---") { flushParagraph(); continue; }
    if (/^#(?!#)/.test(line)) { continue; } // Ignora H1 pois já usamos como título na tela

    if (line.startsWith("## ")) {
      flushParagraph();
      blocks.push(
        <h2 key={key++} className="text-2xl md:text-3xl font-bold text-white mt-12 mb-6">
          {line.slice(3)}
        </h2>
      );
      continue;
    }

    if (line.startsWith("### ")) {
      flushParagraph();
      blocks.push(
        <h3 key={key++} className="text-xl font-bold text-white mt-8 mb-4">
          {line.slice(4)}
        </h3>
      );
      continue;
    }

    // Identifica e renderiza botões de ação no meio do texto
    const btnMatch = line.match(/^\[Botão:\s*(.+)\]$/i);
    if (btnMatch) {
      flushParagraph();
      blocks.push(
        <div key={key++} className="my-10 flex justify-center lg:justify-start">
          <Button 
            onClick={onAction}
            className="bg-gold hover:bg-gold/90 text-primary-foreground font-bold px-8 py-6 rounded-xl shadow-lg shadow-gold/20 hover:scale-105 transition-transform duration-300"
          >
            {btnMatch[1]}
          </Button>
        </div>
      );
      continue;
    }

    // Linhas de destaque (bold full line)
    const boldLine = line.match(/^\*\*(.+)\*\*$/);
    if (boldLine) {
      flushParagraph();
      blocks.push(
        <p key={key++} className="font-bold text-white text-xl leading-snug mt-8 mb-4 border-l-4 border-gold pl-4">
          {boldLine[1]}
        </p>
      );
      continue;
    }

    paraLines.push(line);
  }

  flushParagraph();
  return blocks;
}

const BlogPostSkeleton = () => (
  <div className="max-w-3xl mx-auto pt-40 pb-24 px-4">
    <div className="w-24 h-6 bg-slate-800 rounded animate-pulse mb-8"></div>
    <div className="w-full h-12 bg-slate-800 rounded animate-pulse mb-6"></div>
    <div className="w-3/4 h-12 bg-slate-800 rounded animate-pulse mb-12"></div>
    <div className="w-full h-[450px] bg-slate-800 rounded-3xl animate-pulse mb-12"></div>
    <div className="space-y-4">
      <div className="w-full h-4 bg-slate-800 rounded animate-pulse"></div>
      <div className="w-full h-4 bg-slate-800 rounded animate-pulse"></div>
      <div className="w-5/6 h-4 bg-slate-800 rounded animate-pulse"></div>
    </div>
  </div>
);

const BlogPostPage = () => {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const { openPopup } = useWhatsAppPopup();

  const handleAction = () => {
    openPopup("generic", "whatsapp");
  };

  useEffect(() => {
    const fetchPost = async () => {
      if (!id) return;
      try {
        const data = await blogService.getPostById(id);
        if (!data) throw new Error("Post não encontrado");
        setPost(data);
      } catch (err) {
        console.error("Erro ao carregar o post:", err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPost();
    window.scrollTo(0, 0);
  }, [id]);

  if (isLoading) return <BlogPostSkeleton />;

  if (error || !post) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col">
        <Header />
        <div className="flex-grow flex flex-col items-center justify-center text-center px-4">
          <AlertCircle className="w-16 h-16 text-red-500/50 mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">Post não localizado</h1>
          <p className="text-slate-400 mb-8 max-w-sm">O conteúdo que você está procurando pode ter sido removido ou o link está incorreto.</p>
          <Link to="/blog">
            <Button className="bg-gold text-primary-foreground hover:bg-gold/90 px-8 py-6 rounded-xl font-bold">
              Voltar para o Blog
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const { title } = getBlogMeta(post);

  const handleShare = async () => {
    const shareData = {
      title,
      text: 'Confira este artigo no Blog da JL Náutica',
      url: window.location.href
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2500);
      }
    } catch (err) {
      console.log('Compartilhamento falhou', err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      
      <article className="pt-32 pb-32 relative overflow-hidden">
        {/* Background Blur */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gold/5 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <Link to="/blog" className="inline-flex items-center text-slate-400 hover:text-gold mb-10 transition-colors group text-sm font-medium">
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              Voltar para publicações
            </Link>
            
            <h1 className="text-3xl md:text-5xl lg:text-5xl font-bold text-white mb-10 leading-tight">
              {title}
            </h1>

            <div className="flex flex-wrap items-center justify-between gap-6 border-y border-white/10 py-5">
              <div className="flex items-center gap-3 text-slate-400 font-medium text-sm">
                <Calendar className="w-4 h-4 text-gold" />
                {format(new Date(post.published_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </div>

              <button
                onClick={handleShare}
                className={`flex items-center gap-2 transition-all px-5 py-2.5 rounded-full text-sm font-bold border ${
                  isCopied
                    ? 'bg-green-500/10 text-green-400 border-green-500/20'
                    : 'bg-white/5 text-slate-300 hover:text-white hover:bg-white/10 border-white/10'
                }`}
              >
                {isCopied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4 text-gold" />}
                {isCopied ? 'Link Copiado!' : 'Compartilhar'}
              </button>
            </div>
          </motion.div>

          {/* Featured Image */}
          {post.image_url && (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="relative rounded-3xl overflow-hidden mb-16 shadow-2xl shadow-gold/5 border border-white/5"
            >
              <img 
                src={post.image_url} 
                alt={title}
                className="w-full h-auto max-h-[600px] object-cover"
              />
            </motion.div>
          )}

          {/* Rendered Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-20"
          >
            {renderBody(post.copy_text || '', handleAction)}
          </motion.div>

          {/* CTA Box (Unifer style) */}
          {post.copy_cta && (
            <div className="mt-16 p-8 md:p-10 rounded-3xl border border-gold/20 bg-gold/5 flex flex-col md:flex-row items-center justify-between gap-8 mb-16 relative overflow-hidden group">
              {/* Animated hover line */}
              <div className="absolute top-0 left-0 w-1.5 h-full bg-gold origin-bottom scale-y-0 group-hover:scale-y-100 transition-transform duration-500" />

              <div className="flex-1 relative z-10">
                <p className="text-xs font-bold text-gold tracking-widest uppercase mb-3">
                  // PRÓXIMO PASSO
                </p>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Pronto para começar sua jornada?
                </h3>
                <p className="text-slate-300 font-medium leading-relaxed">
                  {post.copy_cta.replace(/\*\*(.*?)\*\*/g, '$1')}
                </p>
              </div>

              <div className="flex-shrink-0 w-full md:w-auto relative z-10">
                <Button 
                  onClick={handleAction}
                  className="w-full md:w-auto bg-gold hover:bg-gold/90 text-primary-foreground font-bold px-8 py-6 rounded-full text-xs uppercase tracking-widest shadow-lg shadow-gold/25 transition-all md:hover:-translate-y-1"
                >
                  Falar com Especialista
                </Button>
              </div>
            </div>
          )}

          {/* Hashtags Footer */}
          {post.hashtags && post.hashtags.length > 0 && (
            <div className="pt-8 border-t border-white/10 flex flex-wrap gap-2">
              {post.hashtags.map((tag, i) => (
                <span key={i} className="text-xs font-medium text-slate-400 bg-white/5 px-4 py-2 rounded-full flex items-center gap-1.5 hover:text-gold hover:bg-white/10 transition-all border border-white/5 cursor-default">
                  <Tag size={12} className="text-gold" /> {tag.startsWith('#') ? tag.slice(1) : tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
